import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { login as loginRequest } from '../services/api';

const HRLoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const handleLogin = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    setIsSubmitting(true);

    try {
      const response = await loginRequest({
        email,
        password,
        role: 'hr',
      });

      SessionManager.setSession('hr', response);

      setFeedback({
        type: 'success',
        message: 'Welcome back to HR Dashboard!',
      });

      setTimeout(() => {
        navigate('/hr/dashboard');
      }, 800);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Unable to log in. Please check your credentials.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ==================== LEFT PANEL - Professional Blue Theme ==================== */}
      <div className="hidden lg:flex lg:w-[48%] relative bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyan-500/25 via-blue-500/20 to-transparent blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full bg-gradient-to-tl from-indigo-500/25 via-purple-500/15 to-transparent blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full bg-blue-400/10 blur-2xl"></div>
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNCkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-60 pointer-events-none"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
          {/* Top - Logo */}
          <div>
            <button onClick={() => navigate('/')} className="flex items-center space-x-2 group">
              <span className="text-xl font-bold text-white">Aptirix</span>
              <span className="text-sm text-cyan-400 font-medium">HR Portal</span>
            </button>
          </div>

          {/* Center - Hero content + Image */}
          <div className="flex-1 flex flex-col justify-center space-y-8 py-12">
            <div>
              <div className="inline-block px-3 py-1.5 bg-cyan-400/15 rounded-full text-xs font-semibold text-cyan-300 border border-cyan-400/25 mb-6 tracking-wide">
                HR MANAGEMENT PLATFORM
              </div>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
                Streamline your<br />hiring with <span className="text-cyan-400">AI</span>
              </h1>
              <p className="text-gray-400 text-base max-w-sm leading-relaxed">
                Post jobs, review candidates, conduct AI-powered interviews, and make data-driven hiring decisions.
              </p>
            </div>

            {/* Image card */}
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30 max-w-sm">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop&q=80"
                alt="HR team reviewing candidates"
                className="w-full h-52 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-cyan-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-cyan-400/30">
                    <Icon name="people" size={16} className="text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Smart Candidate Matching</p>
                    <p className="text-gray-400 text-xs">AI-powered ATS scoring</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Icon name="business" size={20} className="text-cyan-400/70" />
                <span className="text-gray-400 text-sm">500+ Companies</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="work" size={20} className="text-cyan-400/70" />
                <span className="text-gray-400 text-sm">10K+ Jobs Posted</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="verified" size={20} className="text-cyan-400/70" />
                <span className="text-gray-400 text-sm">Secure</span>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <p className="text-gray-500 text-xs">© 2026 Aptirix. All rights reserved.</p>
        </div>
      </div>

      {/* ==================== RIGHT PANEL - White Professional Form ==================== */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 sm:px-12 py-12">
        <div className="w-full max-w-[420px]">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <span className="text-xl font-bold text-gray-900">Aptirix</span>
              <span className="text-sm text-blue-600 font-medium">HR Portal</span>
            </button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">HR Sign In</h2>
            <p className="text-gray-500 text-sm">Access your HR dashboard and manage your hiring process</p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <Icon name="mail" size={18} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all text-sm"
                  placeholder="hr@company.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <Icon name="lock" size={18} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-10 pr-11 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all text-sm"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={18} />
                </button>
              </div>
            </div>

            {/* Feedback */}
            {feedback.message && (
              <div className={`text-sm px-4 py-3 rounded-lg flex items-start space-x-2 ${
                feedback.type === 'error'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                <Icon name={feedback.type === 'error' ? 'error' : 'check_circle'} size={18} className="mt-0.5 flex-shrink-0" />
                <span>{feedback.message}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!email || !password || isSubmitting}
              className="w-full h-11 bg-[#0f172a] text-white text-sm font-semibold rounded-lg hover:bg-[#1e293b] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign in to HR Dashboard</span>
              )}
            </button>

            {/* Back to main site */}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium text-center py-2"
            >
              ← Back to Main Site
            </button>
          </form>

          {/* Contact Support */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Need help accessing your account?{' '}
              <a href="mailto:support@aptirix.com" className="text-blue-600 hover:text-blue-700 font-medium">
                Contact Support
              </a>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HRLoginPage;
